<script lang="ts" setup>
import appCan from '@@/bits/app_can'
import { ContentPickerSource } from '@@/bits/content_picker'
import { dir } from '@@/bits/current_dir'
import device from '@@/bits/device'
import { isAppUsing } from '@@/bits/flip'
import { __ } from '@@/bits/intl'
import { recordWhitelistedClick } from '@@/bits/pepin'
import { defineAsyncComponent } from '@@/bits/vue'
import { useContentPickerStore } from '@@/pinia/content_picker'
import { useDarkModeStore } from '@@/pinia/dark_mode'
import { usePostComposerModalStore } from '@@/pinia/post_composer_modal_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceContainerSizeStore } from '@@/pinia/surface_container_size'
import { useSurfaceContentPickerStore } from '@@/pinia/surface_content_picker'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceGroupPickerStore } from '@@/pinia/surface_group_picker'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfaceOnboardingDemoPadletPanelCopyStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_copy_store'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceRequireNoticeStore } from '@@/pinia/surface_require_notice_store'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { AttachmentProps, PostAttributes } from '@@/types'
import { usePostContentPickerSources } from '@@/vuecomposables/post_content_picker_sources'
import { useSurfaceNavbarVisibility } from '@@/vuecomposables/surface_navbar_visibility'
import { useReducedMotionMode } from '@@/vuecomposables/useReducedMotionMode'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

const ContentPicker = defineAsyncComponent(() => import('@@/vuecomponents/ContentPicker.vue'))
const SurfaceAddPostButton = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddPostButton.vue'))
const SurfaceAddPostButtonOld = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddPostButtonOld.vue'))
const SurfaceGroupPicker = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceGroupPicker.vue'))

const { contentSources } = usePostContentPickerSources()

const postComposerModalStore = usePostComposerModalStore()
const contentPickerStore = useContentPickerStore()
const surfaceStore = useSurfaceStore()
const surfaceContentPickerStore = useSurfaceContentPickerStore()
const surfaceDraftsStore = useSurfaceDraftsStore()
const surfaceMapStore = useSurfaceMapStore()
const surfaceDemoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()
const surfaceDemoPadletPanelCopyStore = useSurfaceOnboardingDemoPadletPanelCopyStore()
const { isActionBarMounted } = useSurfaceNavbarVisibility()
const surfacePermissionsStore = useSurfacePermissionsStore()
const surfacePostsStore = useSurfacePostsStore()
const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
const surfaceRequireNoticeStore = useSurfaceRequireNoticeStore()
const windowSizeStore = useWindowSizeStore()
const surfaceCurrentUserStore = useSurfaceCurrentUserStore()
const surfaceSectionsStore = useSurfaceSectionsStore()
const darkModeStore = useDarkModeStore()
const { isDarkMode } = storeToRefs(darkModeStore)

const surfaceWishArrangementStore = useSurfaceWishArrangementStore()

const surfaceGroupPickerStore = useSurfaceGroupPickerStore()
const { isVisible: xGroupPicker, title: groupPickerTitle } = storeToRefs(surfaceGroupPickerStore)

const { showOverFileSizeModal } = surfaceStore
const { uploadFileActiveDraft, updateActiveDraft } = surfaceDraftsStore
const { hidePostContentPicker } = surfaceContentPickerStore
const { windowHeight } = storeToRefs(windowSizeStore)

const surfaceContainerSizeStore = useSurfaceContainerSizeStore()
const { isContainerSmallerThanTabletPortrait } = storeToRefs(surfaceContainerSizeStore)
const { startPickingLocation } = surfaceMapStore
const { startNewPost } = surfacePostsStore
const { isMap, skinAccentColorAsRGB, skinAccentTextColor, buttonDarkMode, backgroundLuminance } =
  storeToRefs(surfaceStore)

/**
 * Opens the wall in a new window for LTI (Learning Tools Interoperability) handling.
 * This is used when a user needs to authenticate or access the wall through an LTI integration.
 * The URL opened is the login and redirect URL stored in the surface store.
 */
const openInNewWindow = (): void => {
  window.open(surfaceStore.loginAndRedirectToWallUrl, '_blank')
}

const onUploadStart = ({ url }: { url?: string }) => {
  if (url) {
    surfacePostsStore.saveContent({ content: { link: url } })
  }
}

const onUploadDone = (url: string, attachmentProps?: AttachmentProps) => {
  contentPickerStore.hideContentPickerPanel()
  const shouldCreateNewPost = ![ContentPickerSource.GoogleDrive, ContentPickerSource.MicrosoftOneDrive].includes(
    attachmentProps?.contentSource,
  )
  surfacePostsStore.saveContent({
    content: { link: url, altText: attachmentProps?.altText, shouldCreateNewPost },
  })
  if (surfaceDraftsStore.activeDraft?.shouldAutoPublish) {
    surfacePostsStore.savePost({ attributes: surfaceDraftsStore.activeDraft })
  }
}

const onUploadReject = (file: File) => {
  showOverFileSizeModal({ file })
}

const onUploadError = () => {
  surfaceDraftsStore.stopUploadFileActiveDraft()
  if (surfaceDraftsStore.activeDraft) {
    surfaceDraftsStore.stopAutoPublishDraft(surfaceDraftsStore.activeDraft.cid)
  }
}

const onUploadProgress = (progress: number) => {
  surfaceDraftsStore.updateActiveDraft({ uploadProgress: progress })
}

const showExtensionRequired = (): void => {
  surfaceRequireNoticeStore.showExtensionRequired()
  surfaceRequireNoticeStore.addAfterHideExtensionRequiredAction(() => {
    contentPickerStore.hideContentPickerPanel()
    hidePostContentPicker()
  })
}

const setCaption = (caption: string) => {
  if (!caption) return

  surfacePostsStore.saveAttachmentCaption({ caption })
}

const handleAddPostButtonClick = (): void => {
  surfaceStore.setAddPostButtonClickStatus(true)
  if (isMap.value) {
    startPickingLocation()
  } else {
    if (surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop && surfaceDemoPadletPanelStore.isPostStep) {
      surfaceDemoPadletPanelStore.setFavoritePlacesSectionAsMostRecentlyTouched()
      // Limit to one post draft at a time
      if (!surfaceDraftsStore.isAnyDraftActive) {
        const attributes: PostAttributes = {
          ...surfaceDemoPadletPanelCopyStore.postData[0],
        }
        startNewPost({ attributes })
      }
    } else {
      startNewPost({})
    }
  }
}

const canShowAddPost = computed(() => {
  const isAppWithNewPosting = device.app && appCan('newPostModal')
  const isActiveDraftExpanded = postComposerModalStore.hasExpandedComposerModal && device.desktop && !device.phone
  const isAPostBeingEdited = !!surfacePostsStore.postBeingEditedCid
  return (
    !isAppWithNewPosting &&
    (surfaceStore.isEmbedded || !surfaceStore.isMobileLayout) &&
    surfacePermissionsStore.canIWrite &&
    !surfaceStore.isReadOnly &&
    (!isAPostBeingEdited || !isActiveDraftExpanded) &&
    surfaceDraftsStore.hasFetchedInitialPostDrafts &&
    // Reason why we can only show the add post button after dockable modal list is mounted:
    // `DockableModalList` has a prop called `initialModalState`. In a normal case, it is set
    // to `Minimized`. This is so that after we fetch post drafts, they appear as minimized.
    // If we show the add post button before the dockable modal list is mounted, and the user
    // clicks on the button to create a new draft, this draft will be considered as a draft
    // we fetch from the API and will appear minimized. This is not the desired behavior.
    // So if we delay showing this button until dockable modal list is mounted, we can avoid
    // the issue as users can only create drafts after dockable modal list is already rendered.
    // However, it is not perfect because we sacrifice Time to Interactive for this.
    // TODO: Improve this fix
    postComposerModalStore.isDockableModalListMounted
  )
})

// #region: add post button tooltip and animation
const xAddPostButton = computed(() => {
  return canShowAddPost.value && !surfaceStore.isSubmissionRequest
})
const { currentUser } = storeToRefs(surfaceCurrentUserStore)
const { isInReducedMotionMode } = useReducedMotionMode(currentUser)
const { hasActiveComposerModal } = storeToRefs(postComposerModalStore)
const shouldAnimateAddPostButton = computed(() => !isInReducedMotionMode.value && !hasActiveComposerModal.value)
const shouldShowAddPostButtonTooltip = computed(() => !isMap.value && surfaceDraftsStore.draftCids.length === 0)
// #endregion

const xContentPicker = computed(() => {
  return (
    (contentPickerStore.xContentSourceMenu === true || contentPickerStore.activeContentPickerPanel !== null) &&
    surfaceContentPickerStore.xPostContentPicker
  )
})

const xSearchBar = computed(() => {
  return contentSources.value
    .flat()
    .filter((source) => source.visible)
    .some((source) =>
      [
        ContentPickerSource.ImageSearch,
        ContentPickerSource.GifSearch,
        ContentPickerSource.VideoSearch,
        ContentPickerSource.AudioSearch,
        ContentPickerSource.WebBrowser,
        ContentPickerSource.LinkInput,
        ContentPickerSource.AiImageSearch,
      ].includes(source.type as ContentPickerSource),
    )
})

const googleDriveUploadKey = computed(() => {
  return surfaceDraftsStore.activeDraft ? `draft:${surfaceDraftsStore.activeDraft.cid}` : undefined
})
</script>

<template>
  <div :dir="dir()" class="z-addpost" @click.stop="recordWhitelistedClick" @keydown.stop="() => {}">
    <Teleport v-if="xContentPicker" to="#content-picker-portal-target">
      <ContentPicker
        :enable-hidden-inputs="device.mobile"
        :preferred-content-source-menu-start-height="500"
        :content-source-menu-list="contentSources"
        :x-search-bar="xSearchBar"
        :is-online="surfaceStore.isOnline"
        :is-lti-embedded="surfaceStore.isLtiEmbedded"
        :is-microsoft-teams-embedded="surfaceStore.isMicrosoftTeamsEmbedded"
        :should-upload-file="!surfaceDraftsStore.isAnyDraftActive"
        :active-draft="surfaceDraftsStore.activeDraft"
        :search-modes="surfaceContentPickerStore.currentSearchModes"
        :default-search-mode="surfaceContentPickerStore.defaultSearchMode"
        :search-suggestions="surfaceContentPickerStore.searchSuggestions"
        :google-drive-upload-key="googleDriveUploadKey"
        :user="surfaceStore.user"
        :wall="surfaceStore.wallAttributes"
        :wall-post-properties="surfacePostPropertiesStore.wallPostProperties"
        :teleport-target="'#surface-container'"
        @open-in-new-window="openInNewWindow"
        @close-content-picker="hidePostContentPicker"
        @upload-start="onUploadStart"
        @upload-done="onUploadDone"
        @upload-reject="onUploadReject"
        @upload-error="onUploadError"
        @upload-progress="onUploadProgress"
        @select-link="onUploadDone"
        @update-active-draft="updateActiveDraft"
        @select-file="uploadFileActiveDraft"
        @show-extension-required="showExtensionRequired"
        @set-caption="setCaption"
      />
    </Teleport>
    <Teleport v-if="isAppUsing('surfaceTopbar') && isActionBarMounted" to="#new-post-button-container">
      <div v-if="xAddPostButton">
        <div
          :class="[
            'relative',
            shouldShowAddPostButtonTooltip && [
              'before:absolute before:end-[calc(100%+1rem)]',
              'before:invisible',
              'before:shadow-elevation-2',
              'before:rounded-lg',
              'before:box-border',
              'before:w-[320px]',
              'before:px-3 before:py-2',
              backgroundLuminance === 'light' ? 'before:bg-overlay-text-bg-d70' : 'before:bg-popover-light',
              backgroundLuminance === 'light' ? 'before:text-light-text-100' : 'before:text-dark-text-100',
              'before:backdrop-blur-[6px]',
              'before:cursor-default before:pointer-events-none',
              'before:text-body-small before:font-sans before:text-start',
              'hover-hover:hover:before:visible',
              'hover-hover:hover:before:content-[attr(data-tooltip)]',
              'has-[:focus-visible]:before:visible',
              'has-[:focus-visible]:before:content-[attr(data-tooltip)]',
            ],
          ]"
          :data-tooltip="
            !isMap ? __('Double click anywhere, drag files in, paste from clipboard, or click here to post.') : ''
          "
        >
          <SurfaceAddPostButton
            id="add-post-button"
            class="pointer-events-auto"
            :label="__('Add a post to the board')"
            :background-color="skinAccentColorAsRGB"
            :text="__('Post')"
            :text-color="skinAccentTextColor"
            :dark-mode="buttonDarkMode"
            :should-animate="shouldAnimateAddPostButton"
            @click="handleAddPostButtonClick"
          />
        </div>
      </div>
    </Teleport>
    <SurfaceAddPostButtonOld v-else-if="xAddPostButton" @click="handleAddPostButtonClick" />
    <SurfaceGroupPicker
      v-if="xGroupPicker"
      :groups="surfaceWishArrangementStore.sortedGroups"
      :excluded-group-ids="surfaceGroupPickerStore.excludedGroupIds"
      :dark-mode="isDarkMode"
      :window-height="windowHeight"
      :full-size="isContainerSmallerThanTabletPortrait"
      :can-create-new-group="surfacePermissionsStore.canCreateNewGroup"
      :post-group-by="surfaceWishArrangementStore.wishGroupBy"
      :create-section="surfaceSectionsStore.createSectionWithName"
      :title="groupPickerTitle"
    />
  </div>
</template>
